// 20. 10. 2026 (owner): the Survivors list (everyone of the season: name, level, crew tag, title, Survivor Rank and a
// coarse activity; search, three sorts, pages of 50), the public profile (Season Score, the Collection book in %, worn
// Legendaries, featured titles, the crew) and Follow (followed survivors on top of the list, their big moments in the
// feed). Never shown about anyone else: the zone, whether they are on a run, the pack and Storage, an exact time of last
// activity (docs/notes/players.md).

import { z } from 'zod';
import type { BodyType } from './characters';
import type { Emblem } from './content/clans';
import type { OutfitRef } from './outfits';
import type { BackgroundId, GearSlot } from './types';

export const PLAYERS_RULES = {
  /** Rows on a page of the list. */
  pageSize: 50,
  /** Survivors one player may follow (the feed reads their moments). */
  maxFollows: 200,
  /** The worn title and up to this many more (the newest first) on the profile. */
  featuredTitles: 5,
  /** A followed survivor's moment stays in the feed this long. */
  momentDays: 7,
  /** "This week": seen in the last 7 days (today: since the server's midnight). */
  weekDays: 7,
} as const;

/** Coarse activity, never a time: today (since the server's midnight), the last 7 days, earlier (or never). */
export type PlayerActivity = 'today' | 'week' | 'earlier';
export type PlayersSort = 'level' | 'score' | 'activity';
export const PLAYERS_SORTS: PlayersSort[] = ['level', 'score', 'activity'];

/** Where a profile was opened from (the analytics of `profile_view`). */
export const PROFILE_FROM = ['list', 'feed', 'encounter', 'report', 'score', 'horde', 'crew', 'other'] as const;
export type ProfileFrom = (typeof PROFILE_FROM)[number];

/** The server's midnight before `now` (its time zone, like the horde and the season's end). */
export function dayStart(now: number): number {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** The bucket of a last-seen time (null: never seen). */
export function activityOf(lastSeen: number | null | undefined, now: number): PlayerActivity {
  if (lastSeen == null) return 'earlier';
  if (lastSeen >= dayStart(now)) return 'today';
  if (lastSeen >= now - PLAYERS_RULES.weekDays * 86_400_000) return 'week';
  return 'earlier';
}

/** The worn title first, then the newest earned ones (no repeats), `PLAYERS_RULES.featuredTitles` at most. */
export function featuredTitles(title: string | null | undefined, titles: readonly string[]): string[] {
  const out: string[] = [];
  if (title) out.push(title);
  for (const x of [...titles].reverse()) if (x && !out.includes(x) && out.length < PLAYERS_RULES.featuredTitles) out.push(x);
  return out;
}

export const playersListSchema = z.object({
  q: z.string().trim().max(40).default(''),
  sort: z.enum(['level', 'score', 'activity']).default('level'),
  page: z.coerce.number().int().min(1).max(100_000).default(1),
});

/** `from` comes with the first look only (a refetch sends none), so a view is counted once. */
export const profileQuerySchema = z.object({ from: z.enum(PROFILE_FROM).optional() });

/** A row of the Survivors list. */
export interface PlayerRowDTO {
  userId: string;
  name: string;
  level: number;
  /** Survivor Rank (level 20; 0 before it). */
  rank: number;
  background: BackgroundId;
  bodyType: BodyType;
  outfit: OutfitRef | null;
  crewTag: string | null;
  title: string | null;
  /** This season's Season Score (0 before the season job counted it). */
  score: number;
  activity: PlayerActivity;
  following: boolean;
  isMe: boolean;
}

/** A page of the Survivors list: followed survivors first, then by the sort. */
export interface PlayersPageDTO {
  rows: PlayerRowDTO[];
  /** Survivors that fit the search. */
  total: number;
  page: number;
  pages: number;
  pageSize: number;
  sort: PlayersSort;
  q: string;
  /** How many survivors the player follows. */
  following: number;
}

/** The public profile. */
export interface PlayerProfileDTO {
  userId: string;
  name: string;
  level: number;
  rank: number;
  background: BackgroundId;
  bodyType: BodyType;
  outfit: OutfitRef | null;
  title: string | null;
  /** The worn title and the newest earned ones. */
  titles: string[];
  activity: PlayerActivity;
  season: { number: number; name: string; score: number; place: number | null; participants: number };
  collection: { found: number; total: number; pct: number };
  /** Legendaries worn now. */
  legendaries: { templateId: string; name: string; slot: GearSlot }[];
  /** Null with crews off or without a crew. */
  crew: { name: string; tag: string; emblem: Emblem; level: number } | null;
  followers: number;
  following: boolean;
  isMe: boolean;
  /** "Invite to crew": `can`, `invited` (already), or null (crews off, not a leader or officer, they have a crew, you). */
  crewInvite: 'can' | 'invited' | null;
}
